import { useEffect, useState } from 'react';
import { Box, Button, Switch, Typography, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { C, RADIUS, TYPE, SX } from './tokens';
import StateChip from './StateChip';

/**
 * One setting: label, plain-English help, current value, control, and who
 * changed it last.
 *
 * Before this, a config toggle was 30-80 lines of bespoke JSX plus a bespoke
 * handler plus a bespoke snackbar, repeated ~50 times across the module -- which
 * is why no two of them behaved the same way.
 *
 * @param label     what the setting does, named for the behaviour not the field
 * @param help      one line. Optional, but write one.
 * @param value     current value, rendered right-aligned when there is no control
 * @param state     CONFIG_STATE key
 * @param audit     { by, at } -- only pass this where the backend actually
 *                  records it (codChangeLogs, paceEnabledBy/At, allocationPublishedBy).
 *                  Never fabricate it: a wrong "changed by" is worse than none.
 * @param control   the input node
 * @param disabled  greys the row
 * @param disabledReason  shown in place of help when disabled -- say *why*
 */
export default function ConfigRow({
    label, help, value, state, audit, control,
    disabled = false, disabledReason, dense = false,
}) {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 2,
            py: dense ? 1.1 : 1.5,
            borderBottom: `1px solid ${C.lineSoft}`,
            '&:last-of-type': { borderBottom: 'none' },
            opacity: disabled ? 0.55 : 1,
        }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={TYPE.rowLabel}>{label}</Typography>
                    {state && <StateChip state={state} />}
                </Box>

                {(disabled && disabledReason) ? (
                    <Typography sx={{ ...TYPE.rowHelp, mt: 0.3, color: C.warn }}>
                        {disabledReason}
                    </Typography>
                ) : help ? (
                    <Typography sx={{ ...TYPE.rowHelp, mt: 0.3, maxWidth: '62ch' }}>{help}</Typography>
                ) : null}

                {audit?.by && (
                    <Typography sx={{ ...TYPE.meta, mt: 0.5 }}>
                        Changed by {audit.by}{audit.at ? ` · ${audit.at}` : ''}
                    </Typography>
                )}
            </Box>

            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.25, pt: 0.1 }}>
                {value != null && !control && (
                    <Typography sx={{ ...TYPE.value, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                )}
                {control}
            </Box>
        </Box>
    );
}

/**
 * A boolean setting that saves the moment you flip it, then offers Undo.
 *
 * The module previously mixed three save patterns on one page -- save-on-toggle,
 * an explicit Save button, and a red "Reset" button that actually meant "clear
 * the form". The rule now: toggles save immediately with an undo toast, forms
 * use Save/Cancel, and never both on one screen.
 *
 * @param onChange  (nextValue) => Promise -- the existing endpoint call, unchanged
 * @param confirm   when set, ask first. Use for settings that change how orders
 *                  are routed, charged, or delivered -- not for reporting flags.
 */
export function ConfigToggle({
    checked, onChange, label, help, state, audit,
    disabled = false, disabledReason, confirm = null, onRequestConfirm,
}) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [busy, setBusy] = useState(false);
    const [local, setLocal] = useState(!!checked);

    // Show the optimistic value until the parent's refetch actually catches up.
    // Deriving straight from `checked` made the switch visibly snap back in the
    // window between the write resolving and the parent re-rendering.
    useEffect(() => { setLocal(!!checked); }, [checked]);
    const shown = local;

    const commit = async (next, { silent = false } = {}) => {
        setLocal(next);
        setBusy(true);
        try {
            await onChange(next);
            if (!silent) {
                enqueueSnackbar(`${label} ${next ? 'enabled' : 'disabled'}`, {
                    variant: 'success',
                    action: (key) => (
                        <Button
                            size="small"
                            onClick={() => { closeSnackbar(key); commit(!next, { silent: true }); }}
                            sx={{ color: '#fff', textTransform: 'none', fontWeight: 700, fontSize: 12 }}
                        >
                            Undo
                        </Button>
                    ),
                });
            }
        } catch (err) {
            setLocal(!next);
            enqueueSnackbar(
                err?.response?.data?.message || `Could not update ${label}. Nothing was changed — try again.`,
                { variant: 'error' },
            );
        } finally {
            setBusy(false);
        }
    };

    const handle = (e) => {
        const next = e.target.checked;
        if (confirm && next !== !!checked && onRequestConfirm) {
            onRequestConfirm({ next, commit: () => commit(next) });
            return;
        }
        commit(next);
    };

    return (
        <ConfigRow
            label={label} help={help} state={state} audit={audit}
            disabled={disabled} disabledReason={disabledReason}
            control={
                busy
                    ? <CircularProgress size={16} sx={{ color: C.accent, mr: 1.5 }} />
                    : <Switch size="small" checked={shown} onChange={handle} disabled={disabled} />
            }
        />
    );
}

/**
 * Save / Cancel footer for settings that are a form rather than a switch.
 * Cancel means cancel -- it does not mean "wipe the fields", which is what the
 * old red "Reset" buttons did.
 */
export function ConfigFormActions({ onSave, onCancel, saving = false, dirty = true, saveLabel = 'Save' }) {
    return (
        <Box sx={{
            display: 'flex', justifyContent: 'flex-end', gap: 1,
            pt: 1.5, mt: 0.5, borderTop: `1px solid ${C.lineSoft}`,
        }}>
            {dirty && (
                <Button size="small" variant="outlined" onClick={onCancel} sx={SX.ctaGhost}>
                    Cancel
                </Button>
            )}
            <Button
                size="small" variant="contained" onClick={onSave}
                disabled={saving || !dirty}
                sx={{ ...SX.cta, minWidth: 84, borderRadius: RADIUS.sm }}
            >
                {saving ? 'Saving…' : saveLabel}
            </Button>
        </Box>
    );
}
